import React, { useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import {MarkAsRead} from '../../../../services/notificationAPIs';


// assets
import { IconBrandTelegram, IconBuildingStore, IconMailbox, IconPhoto } from '@tabler/icons-react';
import User1 from '../../../../assets/images/user/profile.png';
import Nicon from '../../../../assets/images/icons/image.png';
import { RestrictedToRole } from '../../../../roles/RestrictedToRole';

import { formatDistanceToNow } from 'date-fns';

const ABSENCE_TYPES = [
  'SICKNESS',
  'PERSONAL',
  'LATENESS',
  'DAY_OFF',
  'EARLY_DEPARTURE',
  'APPOINTMENT',
  'EMERGENCY',
  'SUSPENSION',
  'UNKNOWN'
];

const extractEmployeeNameFromMessage = (message = '') => {
  if (!message) return '';

  // 1. Backend format: "was submitted by <Name> for <dates>." or "... submitted by <Name>."
  const submittedMatch = message.match(/(?:was\s+)?submitted by\s+([^.\n]+?)(?:\s+for\s+|\.|$)/i);
  if (submittedMatch?.[1]) return submittedMatch[1].trim();

  // 2. Legacy format: "was requested by <Name>"
  const leaveMatch = message.match(/was requested by\s+([^.\n]+?)(?:\s+for\s+|\.|$)/i);
  if (leaveMatch?.[1]) return leaveMatch[1].trim();

  // 3. Legacy format: "recorded by : <Name> for :"
  const absenceMatch = message.match(/recorded by\s*:\s*([^.\n:]+?)(?:\s+for\s*:|\.|$)/i);
  if (absenceMatch?.[1]) return absenceMatch[1].trim();

  return '';
};

const extractAbsenceTypeFromMessage = (message = '') => {
  if (!message) return 'DEFAULT';
  const lower = message.toLowerCase();

  if (lower.includes('sickness')) return 'SICKNESS';
  if (lower.includes('personal')) return 'PERSONAL';
  if (lower.includes('lateness')) return 'LATENESS';
  if (lower.includes('day off') || lower.includes('day_off')) return 'DAY_OFF';
  if (lower.includes('early departure') || lower.includes('early_departure')) return 'EARLY_DEPARTURE';
  if (lower.includes('appointment')) return 'APPOINTMENT';
  if (lower.includes('emergency')) return 'EMERGENCY';
  if (lower.includes('suspension')) return 'SUSPENSION';
  if (lower.includes('unknown')) return 'UNKNOWN';

  // Legacy format: "new <type> recorded by :"
  const match = message.match(/new\s+(.+?)\s+recorded by\s*:/i);
  if (match?.[1]) {
    const normalized = match[1].trim().toUpperCase().replace(/\s+/g, '_');
    return ABSENCE_TYPES.includes(normalized) ? normalized : 'DEFAULT';
  }

  return 'DEFAULT';
};

const extractRequestTypeFromMessage = (message = '') => {
  if (!message) return '';
  const match = message.match(/request of type\s*["']?([^"']+)["']?/i);
  return match?.[1] ? match[1].trim() : '';
};

function ListItemWrapper({ children, onClick }) {
  const theme = useTheme();

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        '&:hover': {
          bgcolor: alpha(theme.palette.grey[200], 0.3)
        }
      }}
    >
      {children}
    </Box>
  );
}
const NotificationItem = ({ notification, title, text, time, who, onRead, id, read }) => {
  const navigate = useNavigate();
  const notif = notification || {};
  const notifId = notif.notificationId || id;
  const notifText = notif.message || text || '';
  const notifTime = notif.timestamp || time || '';
  const notifWho = notif.performedBy || who || 'Unknown';
  const notifContext = notif.context || '';
  const notifTitle = notif.title || '';
  const notifType = notif.type || '';

  // Format the timestamp to relative time (e.g., "2 min ago")
  const formattedTime = notifTime ? formatDistanceToNow(new Date(notifTime), { addSuffix: true }) : '';
  const [isRead, setIsRead] = useState(read);

  const userRole = localStorage.getItem('userRole') || '';
  const isEmployeeRole = userRole === 'EMPLOYEE';

  const lowerText = notifText.toLowerCase();
  const lowerContext = notifContext.toLowerCase();
  const lowerTitle = notifTitle.toLowerCase();

  // Determine if we should show a button and where to navigate
  let navigateTo = null;
  let buttonLabel = 'Go to Requests';
  let navigateState = null;
  const employeeFilter = extractEmployeeNameFromMessage(notifText);

  const isLeave =
    lowerContext.includes('leave') ||
    lowerTitle.includes('leave') ||
    lowerText.includes('leave request');

  const isAbsence =
    lowerContext.includes('absence') ||
    lowerTitle.includes('absence') ||
    lowerText.includes('absence confirmation') ||
    lowerText.includes('recorded by');

  const isNormalRequest =
    lowerContext.includes('general request') ||
    lowerTitle.includes('general request') ||
    lowerText.includes('general request') ||
    (lowerText.includes('request of type') && !isLeave) ||
    (lowerContext.includes('request') && !isLeave && !isAbsence);

  if (isEmployeeRole) {
    if (isLeave) {
      navigateTo = '/leaves';
      buttonLabel = 'Go to My Leaves';
      navigateState = { targetTab: 'EmpRequest' };
    } else if (isAbsence) {
      navigateTo = '/absence';
      buttonLabel = 'Go to My Absences';
      navigateState = { targetTab: 'absences' };
    } else if (isNormalRequest) {
      navigateTo = '/requests';
      buttonLabel = 'Go to My Requests';
      navigateState = { activeTab: 'myRequests' };
    }
  } else {
    if (isLeave) {
      navigateTo = '/vacation-requests';
      buttonLabel = 'Go to Requests';
      navigateState = { employeeFilter, status: 'pending' };
    } else if (isAbsence) {
      navigateTo = '/unconfirmed';
      buttonLabel = 'Go to Requests';
      navigateState = {
        absenceType: extractAbsenceTypeFromMessage(notifText),
        employeeFilter,
        status: 'pending'
      };
    } else if (isNormalRequest) {
      navigateTo = '/requests';
      buttonLabel = 'Go to Requests';
      navigateState = {
        employeeFilter,
        requestType: extractRequestTypeFromMessage(notifText),
        status: 'pending',
        activeTab: 'requests'
      };
    }
  }

  const handleRead = async () => {
    try {
      await onRead(notifId);
      setIsRead(true);
    } catch (e) {
      console.error("Failed to mark as read", e);
    }
  };

  const handleNavigate = async (e) => {
    if (e) e.stopPropagation();
    await handleRead();
    if (navigateTo) {
      navigate(navigateTo, { state: navigateState });
    }
  };

  const displayTitle =
    notifTitle ||
    (isLeave
      ? 'Leave Request'
      : isAbsence
      ? 'Absence Request'
      : isNormalRequest
      ? 'General Request'
      : title && title !== 'Info' && title !== 'INFO'
      ? title
      : notifType || 'Notification');

  return (
    <ListItemWrapper onClick={handleNavigate}>
      <ListItem
        alignItems="center"
        disablePadding
        secondaryAction={
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
            <Typography variant="caption">{formattedTime}</Typography>
          </Stack>
        }
      >
        <ListItemAvatar>
          <img src={Nicon} height={'30px'} width={'30px'} style={{marginLeft:5}}/>
        </ListItemAvatar>

        <ListItemText primary={displayTitle} />
      </ListItem>
      <Stack spacing={2} sx={{ pl: 7 }}>
        <Typography variant="subtitle2">{notifText}</Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {/* Conditionally render button */}
          {navigateTo && (
            <Button
              variant="contained"
              size="small"
              onClick={handleNavigate}
            >
              {buttonLabel}
            </Button>
          )}
        </Stack>
      </Stack>
      <RestrictedToRole allowedRoles={['MANAGER', 'HR']}>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>{`PerformedBy ${notifWho}`} </Typography>
      </RestrictedToRole>
    </ListItemWrapper>
  );
};

// Mail Notification Item (also updated to format time)
const MailNotificationItem = ({ text, time }) => {
  const formattedTime = formatDistanceToNow(new Date(time), { addSuffix: true });

  return (
    <ListItemWrapper>
      <ListItem
        alignItems="center"
        disablePadding
        secondaryAction={
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
            <Typography variant="caption">{formattedTime}</Typography>
          </Stack>
        }
      >
        <ListItemAvatar>
          <Avatar
            sx={{
              color: 'primary.dark',
              bgcolor: 'primary.light'
            }}
          >
            <IconMailbox stroke={1.5} size="20px" />
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary={<Typography variant="subtitle1">Check Your Mail.</Typography>} />
      </ListItem>
      <Stack spacing={2} sx={{ pl: 7 }}>
        <Typography variant="subtitle2">{text}</Typography>
        <Button variant="contained" endIcon={<IconBrandTelegram stroke={1.5} size={20} />} sx={{ width: 'min-content' }}>
          Mail
        </Button>
      </Stack>
    </ListItemWrapper>
  );
};

// ==============================|| NOTIFICATION LIST ITEM ||============================== //

export default function NotificationList({notifications, AXONotifications, setNotifications}) {
  const containerSX = { pl: 7 };

  const markNotificationAsRead = async (id) => {
    try {
      const response = await MarkAsRead(id);
      console.log('Mark as read response:', response);
    
      // Update the notification's read status instead of removing it
      setNotifications(prev => prev.map(n => 
        n.notificationId === id 
          ? { ...n, userNotifications: [{ ...n.userNotifications[0], read: true }] }
          : n
      ));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  console.log(`AXO NOTIFICATIONS LIST:${AXONotifications}`)

  return (
    <List sx={{ width: '100%', maxWidth: { xs: 300, md: 330 }, py: 0 }}>
      {(AXONotifications || []).map((notification, index) => {
        if (!notification) return null; // Skip null entries

        return (
          <NotificationItem
            key={notification.notificationId || index}
            notification={notification}
            id={notification.notificationId}
            title={notification.title || notification.type || 'Info'}
            text={notification.message || 'No message provided.'}
            time={notification.timestamp || ''}
            who={notification.performedBy || 'Unknown'}
            read={notification.userNotifications?.[0]?.read ?? notification.read}
            onRead={markNotificationAsRead}
          />
        );
      })}
    </List>
  );
}
ListItemWrapper.propTypes = { children: PropTypes.node };

